import { User, UserRole } from '@/types/user';
import { apiService } from './api'; // Import the centralized apiService

interface AuthResponse {
  message: string;
  user: User;
  access_token: string;
  token_type: string;
}

interface EmailVerificationResponse {
  message: string;
}

class AuthService {
  // Register a new user
  async register(name: string, email: string, password: string): Promise<AuthResponse> {
    const data = await apiService.post<AuthResponse>(
      '/auth/register',
      {
        name,
        email,
        password,
        password_confirmation: password,
      },
      { isPublic: true }
    );
    // Store the token for immediate authentication
    localStorage.setItem('access_token', data.access_token);
    return data;
  }

  // Login user
  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      const data = await apiService.post<AuthResponse>(
        '/auth/login',
        {
          email: email,
          password: password,
        },
        { isPublic: true }
      );
      localStorage.setItem('access_token', data.access_token);
      return data;
    } catch (error: any) {
      // Handle specific backend error messages
      if (error.response?.data?.message === 'These credentials do not match our records.') {
        throw new Error('Invalid email or password');
      }
      if (error.response?.data?.message === 'Please verify your email address.') {
        throw new Error('Please verify your email address before logging in');
      }
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  }

  // Verify email with verification code
  async verifyEmail(id: string, hash: string): Promise<EmailVerificationResponse> {
    return apiService.get<EmailVerificationResponse>(`/email/verify/${id}/${hash}`, { isPublic: true });
  }

  // Resend verification email
  async resendVerificationEmail(): Promise<void> {
    await apiService.post('/email/verification-notification');
  }

  // Test login (skip authentication for testing)
  testLogin(email: string, isAdmin: boolean = false): AuthResponse {
    const userId = isAdmin ? 999 : 888;
    const name = isAdmin ? "Test Admin" : "Test Operator";
    const token = "test-token-" + Math.random().toString(36).substring(2, 10);
    
    const user: User = {
      id: userId,
      email: email,
      name: name,
      role: isAdmin ? 'admin' : 'operator',
      roles: isAdmin ? ['admin'] : ['operator'],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      email_verified_at: new Date().toISOString() // Test users are verified
    };
    
    const authResponse: AuthResponse = {
      message: "Test login successful",
      user: user,
      access_token: token,
      token_type: "bearer"
    };
    
    // Store the token
    localStorage.setItem('access_token', token);
    localStorage.setItem('user', JSON.stringify(user));
    
    return authResponse;
  }

  // Get current user
  async getCurrentUser(): Promise<User> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token found');
    }
    try {
      return await apiService.get<User>('/auth/user');
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token is invalid, clear it
        localStorage.removeItem('access_token');
        throw new Error('Authentication token expired');
      }
      throw new Error('Failed to get user info');
    }
  }

  // Logout user
  async logout(): Promise<void> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return;
    }
    
    try {
      await apiService.post('/auth/logout');
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local storage even if API call fails
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  // Check if user email is verified
  isEmailVerified(user: User): boolean {
    return !!user.email_verified_at;
  }

  // Get user roles
  getUserRoles(user: User): UserRole[] {
    if (user.roles && user.roles.length > 0) {
      return user.roles;
    }
    if (user.role) {
      return [user.role];
    }
    return ['guest']; // Default role
  }

  // Check if user has specific role
  hasRole(user: User, role: UserRole): boolean {
    const roles = this.getUserRoles(user);
    return roles.includes(role);
  }

  // Check if user has any of the specified roles
  hasAnyRole(user: User, roles: UserRole[]): boolean {
    const userRoles = this.getUserRoles(user);
    return roles.some(role => userRoles.includes(role));
  }

  // Get the primary role for a user
  getPrimaryRole(user: User): UserRole {
    const roles = this.getUserRoles(user);
    if (roles.includes('admin')) return 'admin';
    if (roles.includes('operator')) return 'operator';
    return 'guest';
  }

  async impersonate(userId: string): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>(`/admin/users/${userId}/impersonate`);
    return response;
  }

  async stopImpersonating(): Promise<void> {
    await apiService.post('/admin/users/stop-impersonating');
  }
}

export const authService = new AuthService();
